import Breadcrumb from '~/src/common/components/Breadcrumb';
import Icon from '~/src/common/components/Icon';
import NumberPicker from '~/src/common/components/NumberPicker/NumberPicker';
import I18n from '~/src/common/services/I18n';
import { formatPrice } from '~/src/common/utils/prices';
import { useGetCart } from '~/src/queries/api-ecom/generated/api-ecom';
import { GetRecipeByIdOrSlug200 } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import { MAXIMUM_RECIPE_SERVINGS } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/constants';
import { useRecipeScreenAnalyticsContext } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/context';
import { ChangeServingsAnalyticsInfo } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/hooks/useServings';
import { ClientRecipeIngredientProduct } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/types';
import getAvailableArticlesFromIngredients from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/utils/getAvailableArticlesFromIngredients';

import * as GS from '../layout';

import * as S from './layout';

interface Props {
  recipe: GetRecipeByIdOrSlug200;
  ingredients: ClientRecipeIngredientProduct[];
  servings: number;
  totalPrice: number;
  isLoading: boolean;
  isAdding: boolean;
  onAddAllArticles: (source: 'ingredients list' | 'principal ATC') => Promise<void>;
  onChangeServings: (servings: number, info: ChangeServingsAnalyticsInfo) => void;
}

const RecipeHeaderPane = ({
  recipe,
  ingredients,
  servings,
  totalPrice,
  isLoading,
  isAdding,
  onChangeServings,
  onAddAllArticles,
}: Props) => {
  const { name, cookingTime, preparationTime, cookTypes, recipeType } = recipe;

  const { categoryName, subcategoryName } = useRecipeScreenAnalyticsContext();

  const handleServingsChange = (value: number) => {
    onChangeServings(value, {
      recipe,
      categoryName,
      subcategoryName,
      location: 'near CTA',
    });
  };

  const handleAddAllArticles = () => {
    void onAddAllArticles('principal ATC');
  };

  const { data: cart } = useGetCart();

  const isButtonEnabled = getAvailableArticlesFromIngredients(
    ingredients,
    cart?.products ?? [],
  ).some(i => {
    const cartQuantity = cart?.products?.find(p => p.id === i.id)?.quotation2.count.quantity ?? 0;

    return i.availableQuantity - cartQuantity >= i.packSize;
  });

  return (
    <S.ProductHeaderPaneContainer>
      <GS.MobileBreadcrumbsContainer>
        <Breadcrumb item={recipe} />
      </GS.MobileBreadcrumbsContainer>
      <GS.StickyContent>
        <GS.StickyContentGroup>
          <GS.StickyContentItem>
            {cookTypes.map(tag => (
              <GS.LightGreenTag key={tag}>{tag}</GS.LightGreenTag>
            ))}
            <GS.StyledTypeTag recipeType={recipeType} />
          </GS.StickyContentItem>
          <GS.CatalogItemName>{name}</GS.CatalogItemName>
        </GS.StickyContentGroup>
        <S.AddToCartContainer>
          <NumberPicker
            value={servings}
            minimumValue={1}
            maximumValue={MAXIMUM_RECIPE_SERVINGS}
            maximumValueReachedText={I18n.t('recipe-screen.max-portions-reached')}
            onChange={handleServingsChange}>
            {({ value }) => (
              <S.PortionsText>
                <span>{value}</span>
                <span>{I18n.t('recipe-screen.portion-picker', { count: value })}</span>
              </S.PortionsText>
            )}
          </NumberPicker>
          <S.AddToCartButton
            disabled={!isButtonEnabled}
            onClick={handleAddAllArticles}
            icon="basket-normal"
            loading={isAdding}>
            {I18n.t('common.add-to-cart-with-icon')}
          </S.AddToCartButton>
        </S.AddToCartContainer>
        <S.InformationRow>
          <div>
            <Icon name="cooking-hat" size={24} color="PRIMARY" />
            <p>{I18n.t('recipe-screen.preparation')}</p>
            <p>{I18n.t('recipe-screen.duration-time', { duration: preparationTime })}</p>
          </div>
          <div>
            <Icon name="saucepan" size={24} color="PRIMARY" />
            <p>{I18n.t('recipe-screen.cooking')}</p>
            <p>{I18n.t('recipe-screen.duration-time', { duration: cookingTime })}</p>
          </div>
          <div>
            <Icon name="euro-normal" size={24} color="PRIMARY" />
            <p>{I18n.t('recipe-screen.portion')}</p>
            <p>{isLoading ? '-' : formatPrice(totalPrice / servings)}</p>
          </div>
        </S.InformationRow>
      </GS.StickyContent>
    </S.ProductHeaderPaneContainer>
  );
};

export default RecipeHeaderPane;
